import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PricingCard } from "./PricingCard";
import { ArrowRight } from "lucide-react";
import { TeamPlanDialog } from "./TeamPlanDialog";
import { usePolarCheckout } from "@/hooks/usePolarCheckout";
import { POLAR_PRODUCTS } from "@/config/polar";
import { useAuth } from "@/hooks/useAuth";

export const PricingCards = ({ billingCycle }: { billingCycle: "monthly" | "yearly" }) => {
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const { startCheckout, loading } = usePolarCheckout();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleProCheckout = () => {
    if (!user) {
      navigate("/auth/signup");
      return;
    }
    startCheckout({
      productId: billingCycle === "yearly" ? POLAR_PRODUCTS.pro_yearly : POLAR_PRODUCTS.pro_monthly,
      plan: "pro",
      billingCycle,
    });
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        <PricingCard
          tier="Free"
          price={0}
          billingCycle={billingCycle}
          features={[
            "3 uploads, pastes, and records / day",
            "5 AI chats / day (10 / month with Learn+ mode)",
            "10 quiz answers / day",
            "2 practice exams / month",
            "Upload files up to 100 pages / 10 MB",
          ]}
          buttonText="Get Started"
        />

        <PricingCard
          tier="Pro"
          price={billingCycle === "yearly" ? 45 : 75}
          badge="Popular"
          billingCycle={billingCycle}
          features={[
            "Unlimited uploads, pastes, and records",
            "Unlimited AI chats (100 / day with Study+ mode)",
            "Unlimited quiz generation",
            "Unlimited practice exams",
            "Upload files up to 2000 pages / 300 MB",
          ]}
          buttonText={loading ? "Loading..." : "Start your 7 days free trial"}
          buttonIcon={loading ? undefined : ArrowRight}
          highlight
          onButtonClick={handleProCheckout}
        />

        <PricingCard
          tier="Team"
          price={billingCycle === "yearly" ? 34 : 56}
          billingCycle={billingCycle}
          isTeam={true}
          features={[
            { text: "Everything in Pro +", bold: true },
            { text: "Discounts scale with team size", boldWords: ["team"] },
            { text: "Centralized team billing", bold: true },
            { text: "Shared rooms", boldWords: ["Shared"] },
            {
              text: "Add, remove and set custom permissions for team members",
              boldWords: ["custom", "team"],
            },
          ]}
          buttonText="Choose Team"
          buttonIcon={ArrowRight}
          onButtonClick={() => setTeamDialogOpen(true)}
        />
      </div>

      <TeamPlanDialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen} />
    </>
  );
};
